import { ref, computed, watch, type Ref } from 'vue';
import { useDatePicker } from './useDatePicker';

export type ViewMode = 'days' | 'months' | 'years';

export interface DateRangePickerOptions {
  startDate: Ref<Date | null>;
  endDate: Ref<Date | null>;
  isOpen: Ref<boolean>;
}

export function useDateRangePicker(options: DateRangePickerOptions) {
  const { startDate: propsStartDate, endDate: propsEndDate, isOpen } = options;

  const tempStartDate = ref<Date | null>(propsStartDate.value);
  const tempEndDate = ref<Date | null>(propsEndDate.value);
  const viewMode = ref<ViewMode>('days');
  const yearsRangeStart = ref(Math.floor(new Date().getFullYear() / 10) * 10);

  const { currentDate, calendarDays, goToPrevMonth, goToNextMonth } = useDatePicker(new Date());

  watch(propsStartDate, (newDate) => {
    tempStartDate.value = newDate;
  });

  watch(propsEndDate, (newDate) => {
    tempEndDate.value = newDate;
  });

  watch(isOpen, (open) => {
    if (open) {
      tempStartDate.value = propsStartDate.value;
      tempEndDate.value = propsEndDate.value;
      viewMode.value = 'days';
      if (propsStartDate.value) {
        currentDate.value = new Date(propsStartDate.value);
      } else {
        currentDate.value = new Date();
      }
    }
  });

  const currentYear = computed(() => currentDate.value.getFullYear());

  const yearsRange = computed(() => {
    const years: number[] = [];
    for (let i = 0; i < 10; i++) {
      years.push(yearsRangeStart.value + i);
    }
    return years;
  });

  const goToPrevYear = () => {
    currentDate.value = new Date(
      currentDate.value.getFullYear() - 1,
      currentDate.value.getMonth(),
      1
    );
  };

  const goToNextYear = () => {
    currentDate.value = new Date(
      currentDate.value.getFullYear() + 1,
      currentDate.value.getMonth(),
      1
    );
  };

  const goToPrevYearsRange = () => {
    yearsRangeStart.value -= 10;
  };

  const goToNextYearsRange = () => {
    yearsRangeStart.value += 10;
  };

  const isSelectingEndDate = computed(() => {
    return tempStartDate.value !== null && tempEndDate.value === null;
  });

  const isDateDisabled = (date: Date) => {
    if (!isSelectingEndDate.value) return false;
    if (!tempStartDate.value) return false;

    const startTime = new Date(
      tempStartDate.value.getFullYear(),
      tempStartDate.value.getMonth(),
      tempStartDate.value.getDate()
    ).getTime();
    const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

    return dateTime < startTime;
  };

  const selectDate = (date: Date) => {
    if (isDateDisabled(date)) return;

    if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
      tempStartDate.value = date;
      tempEndDate.value = null;
    } else {
      tempEndDate.value = date;
    }
  };

  const selectMonth = (monthIndex: number) => {
    const year = currentDate.value.getFullYear();
    const startOfMonth = new Date(year, monthIndex, 1);
    const endOfMonth = new Date(year, monthIndex + 1, 0);

    tempStartDate.value = startOfMonth;
    tempEndDate.value = endOfMonth;
    viewMode.value = 'days';
    currentDate.value = new Date(year, monthIndex, 1);
  };

  const selectYear = (year: number) => {
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year, 11, 31);

    tempStartDate.value = startOfYear;
    tempEndDate.value = endOfYear;
    currentDate.value = new Date(year, currentDate.value.getMonth(), 1);
    viewMode.value = 'months';
  };

  const isDateSelected = (date: Date) => {
    if (!tempStartDate.value) return false;

    const dateTime = date.getTime();
    const startTime = tempStartDate.value.getTime();

    if (!tempEndDate.value) {
      return (
        date.getDate() === tempStartDate.value.getDate() &&
        date.getMonth() === tempStartDate.value.getMonth() &&
        date.getFullYear() === tempStartDate.value.getFullYear()
      );
    }

    const endTime = tempEndDate.value.getTime();
    return dateTime >= startTime && dateTime <= endTime;
  };

  const isRangeStart = (date: Date) => {
    if (!tempStartDate.value) return false;
    return (
      date.getDate() === tempStartDate.value.getDate() &&
      date.getMonth() === tempStartDate.value.getMonth() &&
      date.getFullYear() === tempStartDate.value.getFullYear()
    );
  };

  const isRangeEnd = (date: Date) => {
    if (!tempEndDate.value) return false;
    return (
      date.getDate() === tempEndDate.value.getDate() &&
      date.getMonth() === tempEndDate.value.getMonth() &&
      date.getFullYear() === tempEndDate.value.getFullYear()
    );
  };

  const isMonthSelected = (monthIndex: number) => {
    if (!tempStartDate.value || !tempEndDate.value) return false;
    const year = currentDate.value.getFullYear();
    return (
      tempStartDate.value.getFullYear() === year &&
      tempStartDate.value.getMonth() === monthIndex &&
      tempEndDate.value.getFullYear() === year &&
      tempEndDate.value.getMonth() === monthIndex
    );
  };

  const isYearSelected = (year: number) => {
    if (!tempStartDate.value || !tempEndDate.value) return false;
    return (
      tempStartDate.value.getFullYear() === year &&
      tempEndDate.value.getFullYear() === year &&
      tempStartDate.value.getMonth() === 0 &&
      tempStartDate.value.getDate() === 1 &&
      tempEndDate.value.getMonth() === 11 &&
      tempEndDate.value.getDate() === 31
    );
  };

  const toggleViewMode = () => {
    if (viewMode.value === 'days') {
      viewMode.value = 'months';
    } else if (viewMode.value === 'months') {
      viewMode.value = 'years';
    } else {
      viewMode.value = 'days';
    }
  };

  const headerTitle = computed(() => {
    if (viewMode.value === 'days') {
      const month = currentDate.value.toLocaleDateString('pt-BR', { month: 'long' });
      const year = currentDate.value.getFullYear();
      return `${month} ${year}`;
    } else if (viewMode.value === 'months') {
      return currentYear.value.toString();
    } else {
      return `${yearsRangeStart.value} - ${yearsRangeStart.value + 9}`;
    }
  });

  const handlePrev = () => {
    if (viewMode.value === 'days') {
      goToPrevMonth();
    } else if (viewMode.value === 'months') {
      goToPrevYear();
    } else {
      goToPrevYearsRange();
    }
  };

  const handleNext = () => {
    if (viewMode.value === 'days') {
      goToNextMonth();
    } else if (viewMode.value === 'months') {
      goToNextYear();
    } else {
      goToNextYearsRange();
    }
  };

  const resetToProps = () => {
    tempStartDate.value = propsStartDate.value;
    tempEndDate.value = propsEndDate.value;
  };

  const canApply = computed(() => {
    return tempStartDate.value !== null;
  });

  return {
    tempStartDate,
    tempEndDate,
    viewMode,
    currentDate,
    calendarDays,
    yearsRange,
    headerTitle,
    canApply,

    isDateDisabled,
    isDateSelected,
    isRangeStart,
    isRangeEnd,
    isMonthSelected,
    isYearSelected,

    selectDate,
    selectMonth,
    selectYear,
    toggleViewMode,
    handlePrev,
    handleNext,
    resetToProps,
  };
}
